<template>
  <modal
    id="keyboard-shortcuts-modal"
    data-test="graphics-keyboard-shortcuts-modal"
    :header="true"
    :footer="false"
    wide
    tall
  >
    <template #modal-header>
      Keyboard shortcuts
      <button
        class="modal-close"
        data-test="graphics-keyboard-shortcuts-close"
        @click="closeKeyboardShortcuts"
      >
        <span class="fa-solid fa-xmark"></span>
      </button>
    </template>
    <template #modal-body>
      <div>
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          data-test="graphics-keyboard-shortcuts-search"
          placeholder="Search Action..."
          class="shortcuts-search"
        >
        <div class="platform-toggle">
          <button
            type="button"
            class="platform-toggle__btn"
            :class="{ 'platform-toggle__btn--active': activePlatform === 'Windows' }"
            data-test="keyboardshortcuts-button-windows"
            @click="activePlatform = 'Windows'"
          >
            Windows
          </button>
          <button
            type="button"
            class="platform-toggle__btn"
            :class="{ 'platform-toggle__btn--active': activePlatform === 'Mac' }"
            data-test="keyboardshortcuts-button-mac"
            @click="activePlatform = 'Mac'"
          >
            Mac
          </button>
        </div>
      </div>

      <table class="keyboard-shortcuts-table">
        <tbody>
        <tr>
          <th>Action</th>
          <th>Keys ({{ activePlatform }})</th>
        </tr>
        <tr
          v-for="(shortcut, index) in filteredShortcuts"
          :key="index"
          :data-test="`graphics-keyboard-shortcut-${index}`"
        >
          <td>{{ shortcut.action }}</td>
          <td
            :key="'keys-' + index"
            class="shortcut-keys"
          >
            <span
              v-for="(key, keyIndex) in shortcut.keys"
              :key="keyIndex"
            >
              <span class="shortcut-keys__key">{{ key }}</span>
              <template v-if="keyIndex !== shortcut.keys.length - 1">
                <span class="shortcut-keys__separator"> + </span>
              </template>
            </span>
          </td>
        </tr>
        </tbody>
      </table>
    </template>
  </modal>
</template>

<script>
import ModalComponent from './Modal/Modal.vue';

export default {
  emits: ['modal'],
  components: {
    modal: ModalComponent,
  },
  data() {
    return {
      activePlatform: 'Windows', // 'Mac' | 'Windows'
      keyboardShortcuts: [
        { action: 'Move (nudge) up', keysMac: ['↑'], keysWindows: ['↑'] },
        { action: 'Move (nudge) down', keysMac: ['↓'], keysWindows: ['↓'] },
        { action: 'Move (nudge) left', keysMac: ['←'], keysWindows: ['←'] },
        { action: 'Move (nudge) right', keysMac: ['→'], keysWindows: ['→'] },
        { action: 'Ignore groups', keysMac: ['option (hold)'], keysWindows: ['alt (hold)'] },

        { action: 'Copy', keysMac: ['⌘', 'C'], keysWindows: ['ctrl', 'C'] },
        { action: 'Paste', keysMac: ['⌘', 'V'], keysWindows: ['ctrl', 'V'] },
        { action: 'Group multiple elements', keysMac: ['⌘', 'G'], keysWindows: ['ctrl', 'G'] },
        { action: 'Ungroup multiple elements', keysMac: ['⌘', '⇧', 'G'], keysWindows: ['ctrl', '⇧', 'G'] },

        { action: 'Undo', keysMac: ['⌘', 'Z'], keysWindows: ['ctrl', 'Z'] },
        { action: 'Redo', keysMac: ['⌘', '⇧', 'Z'], keysWindows: ['ctrl', '⇧', 'Z'] },

        { action: 'Toggle safety area', keysMac: ['⌘', ';'], keysWindows: ['ctrl', ';'] },
        { action: 'Toggle rulers and guides', keysMac: ['⇧', 'R'], keysWindows: ['⇧', 'R'] },

        { action: 'Pan (grab) Canvas', keysMac: ['⌘', 'space bar'], keysWindows: ['ctrl', 'space bar', 'left mouse'] },
        { action: 'Pan (grab) Canvas', keysMac: ['hold middle mouse button'], keysWindows: ['hold middle mouse button'] },

        { action: 'Toggle to pan (grab) mode', keysMac: ['H'], keysWindows: ['H'] },
        { action: 'Toggle to select (pointer) mode', keysMac: ['V'], keysWindows: ['V'] },

        { action: 'Zoom canvas in', keysMac: ['⌘', '+'], keysWindows: ['ctrl', '+'] },
        { action: 'Zoom canvas out', keysMac: ['⌘', '-'], keysWindows: ['ctrl', '-'] },
        {
          action: 'Zoom canvas in and out',
          keysMac: ['⌘', 'mouse wheel'],
          keysWindows: ['ctrl', 'mouse wheel'],
        },
        { action: 'Zoom mode to fit screen', keysMac: ['⌘', '0'], keysWindows: ['ctrl', '0'] },

        { action: 'Duplicate selected element', keysMac: ['⌘', 'D'], keysWindows: ['ctrl', 'D'] },
        {
          action: 'Locks element aspect ratio',
          keysMac: ['⇧', 'drag resize handle'],
          keysWindows: ['⇧', 'drag resize handle'],
        },
      ],
      searchQuery: '',
    };
  },
  computed: {
    displayShortcuts() {
      const isMac = this.activePlatform === 'Mac';
      return this.keyboardShortcuts.map((shortcut) => ({
        ...shortcut,
        keys: isMac ? shortcut.keysMac : shortcut.keysWindows,
      }));
    },
    sortedShortcuts() {
      return this.displayShortcuts.slice().sort((a, b) => a.action.localeCompare(b.action));
    },
    filteredShortcuts() {
      const q = this.searchQuery.toLowerCase();
      return this.sortedShortcuts.filter((shortcut) => shortcut.action.toLowerCase().includes(q));
    },
  },
  methods: {
    closeKeyboardShortcuts() {
      this.$emit('modal', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.shortcuts-search {
  width: 50%;
  padding: 10px 10px 10px 40px;
  border-radius: 30px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  top: 23px;
  left: 45px;
  font-size: 16px;
  color: #e0e0e0;
}

.platform-toggle {
  margin-top: 10px;
}

.platform-toggle__btn {
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 12px;
  background: #ffffff;
  margin-right: 8px;
  cursor: pointer;
}

.platform-toggle__btn--active {
  background: #333333;
  color: #ffffff;
  border-color: #333333;
}

.keyboard-shortcuts-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  border-top: none;
  font-size: 14px;

  th {
    text-align: left;
    padding: 10px 15px;

    &:first-child {
      border-radius: 5px 0 0 0;
    }

    &:last-child {
      border-radius: 0 5px 0 0;
      text-align: right;
    }
  }

  td {
    padding: 10px 15px;

    &:last-child {
      text-align: right;
    }
  }

  .shortcut-keys__key {
    background: #f5f5f5;
    padding: 5px 10px;
    border-radius: 3px;
    font-size: 1.25rem;
    box-shadow: 0 0 0 1px #e0e0e0;
  }
}
</style>
